"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ShieldCheck, Clock, MapPin, CircleDollarSign, Wrench, CircleCheck as CheckCircle2, CalendarCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const trustPoints = [{
  title: "24/7 Rapid Emergency Response",
  description: "Plumbing disasters don't wait for business hours. Our dispatch line is open around the clock for immediate local triage.",
  attribution: "Emergency Dispatch Standard",
  badge: "24/7 Service",
  icon: Clock
}, {
  title: "Clear, Upfront Pricing",
  description: "No hidden surprise fees or pressure tactics. We diagnose the issue and confirm exact pricing before beginning any work.",
  attribution: "Transparent Pricing Policy",
  badge: "No Surprises",
  icon: CircleDollarSign
}, {
  title: "Dedicated Local Jonesboro Team",
  description: "Based right here in Jonesboro, GA, our plumbers know local infrastructure and respond immediately without regional delays.",
  attribution: "Local Presence Commitment",
  badge: "Jonesboro, GA",
  icon: MapPin
}, {
  title: "Direct Phone Diagnostic Support",
  description: "Speak directly with plumbing professionals ready to assess your emergency instantly. No waiting on unmonitored email queues.",
  attribution: "Direct Response Guarantee",
  badge: "Direct Line",
  icon: Wrench
}];
export default function Trust() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="why-homeowners-trust-us" data-nav="light" className="relative bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border selection:bg-accent selection:text-accent-foreground">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-4 border border-border/60">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <span>Verified Company Commitments</span>
          </div>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground text-balance">
            Why Homeowners Trust Our Emergency Plumbing Response
          </h2>
          
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            When water is flooding your property or drains back up, you need predictable, fast, and accountable service. Here is how Jonesboro Emergency Plumber handles every single call.
          </p>
        </div>

        {/* Trust Statement Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {trustPoints.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} data-index={index} variants={cardVariants} className="relative bg-card rounded-xl border border-border p-6 md:p-8 flex flex-col justify-between border-l-4 border-l-accent shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
                <div>
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center shrink-0 border border-border/50">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-muted text-muted-foreground border border-border/40">
                      {item.badge}
                    </span>
                  </div>

                  <h3 className="text-lg md:text-xl font-bold font-default text-card-foreground mb-2.5">
                    {item.title}
                  </h3>

                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-border/70 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-xs md:text-sm font-medium text-foreground">
                      {item.attribution}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground font-mono">
                    Jonesboro, GA
                  </span>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Conversion Action Bar */}
        <div className="mt-12 md:mt-16 bg-card border border-border rounded-2xl p-6 md:p-8 flex flex-col lg:flex-row items-center justify-between gap-6 shadow-sm">
          <div className="space-y-1 text-center lg:text-left">
            <h3 className="text-lg md:text-xl font-bold font-default text-foreground">
              Facing an active leak, clog, or water heater failure?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Direct phone support is standing by 24/7 across Jonesboro, GA.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto shrink-0">
            <Button asChild className="w-full sm:w-auto rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold px-6 py-6 text-base shadow-sm hover:brightness-105 transition-[filter,box-shadow]">
              <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="w-5 h-5 text-accent-foreground" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>

            <Button asChild variant="default" className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 py-6 text-base">
              <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                <CalendarCheck className="w-5 h-5 text-primary-foreground" />
                <span>Request Service</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}